import { type TFunction } from 'i18next'

import { type Truck } from '../../../common/truck'
import { type Quote } from './models/quote.model'
import { type QuoteJob } from './models/subModels/quoteJob.model'
import { ChargeDescription } from '../../invoices'
import { ignoreTimezone } from '../../../utils/date'

/**
 * return constraints of all jobs of a quote
 * if a jobIndex is provided, we returns only constraints of this job
 */
export const getQuoteConstraints = (quote: Quote, jobIndex?: number) => {
  return Array.from(new Set(quote.jobs
    .filter((_, index) => jobIndex === undefined || jobIndex === index)
    .map(job => job.constraints)
    .filter(c => !!c)
    .flat(),
  ))
}

/**
 * get number of men in a quote
 */
export const getQuoteNbMen = (quote: Quote): number => {
  const nbMen = Array.from(new Set(
    quote.jobs
      .map(job => job.jobResolvedBranch.nbMen)
      .filter(d => !!d),
  ))

  return nbMen[0] ?? 0
}

/**
 * get number of trucks in a quote
 */
export const getQuoteNbTrucks = (quote: Quote) => {
  const nbTrucks = Array.from(new Set(
    quote.jobs
      .map(job => {
        const nbTrucks = job.jobResolvedBranch.nbTrucks
        return nbTrucks?.length ? JSON.stringify(nbTrucks.sort((a, b) => a.value - b.value)) : undefined
      })
      .filter(d => !!d),
  ))
  return nbTrucks[0]
    ? JSON.parse(nbTrucks[0]) as Array<{
      key: Truck
      value: number
    }>
    : undefined
}

/**
 * returns numbers of packing material products in a quote job
 */
export const getQuoteNbProducts = (quoteJob: QuoteJob) => {
  return quoteJob.charges
    .filter(charge => charge.description === ChargeDescription.protectiveMaterialProduct)
    .reduce((acc, curr) => curr.quantity + acc, 0)
}

/**
 * return a label for a quote job based on services
 */
export const getQuoteJobLabel = (quoteJob: QuoteJob, t: TFunction) => {
  const hasMoving = quoteJob.charges.some(charge => charge.description === ChargeDescription.movingLabour)
  const hasPacking = quoteJob.charges.some(charge => charge.description === ChargeDescription.packingLabour)

  const actions = []
  if (hasMoving) {
    actions.push(t('quotes.quote.jobDescription.moving'))
  }
  if (hasPacking) {
    actions.push(t('quotes.quote.jobDescription.packing'))
  }
  return actions.join(' + ')
}

/**
 * returns date of a quote job
 */
export const getQuoteJobDate = (quoteJob: QuoteJob) => {
  const from = quoteJob.date
  const to = quoteJob.dateRange?.pickupWindowEndDate

  return {
    from: ignoreTimezone(from) as Date,
    to: to ? ignoreTimezone(to) : undefined,
  }
}

/**
 * returns delivery windows of a quote
 */
export const getQuoteJobDeliveryWindow = (quoteJob: QuoteJob) => {
  const from = quoteJob.dateRange?.deliveryWindowStartDate
  const to = quoteJob.dateRange?.deliveryWindowEndDate

  if (!from) {
    return
  }

  return {
    from: ignoreTimezone(from) as Date,
    to: to ? ignoreTimezone(to) : undefined,
  }
}
